import { useChangelog, useResolvedTheme } from "@wherdkit/core";
import { useEffect, useRef } from "react";
import { ScrollView, Text, useColorScheme, View } from "react-native";

function useToks() {
  const theme = useResolvedTheme(useColorScheme() ?? "light");
  return {
    text: theme.colors.foreground,
    sub: theme.colors.mutedForeground,
    border: theme.colors.border,
    accent: theme.colors.primary,
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function WhatsNew() {
  const t = useToks();
  const { entries, loading, markAllRead } = useChangelog();

  // Viewing "What's New" clears the unread badge automatically (no manual
  // button). Snapshot which entries were unread when the tab opened so the
  // "new" dots still highlight them for this viewing session.
  const newIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (loading || newIds.current) return;
    newIds.current = new Set(entries.filter((e) => !e.read).map((e) => e.id));
    if (newIds.current.size > 0) void markAllRead();
  }, [loading, entries, markAllRead]);

  if (loading) return <Text style={{ color: t.sub, fontSize: 14 }}>Loading…</Text>;
  if (entries.length === 0) return <Text style={{ color: t.sub, fontSize: 14 }}>Nothing new yet — check back soon.</Text>;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16, paddingBottom: 6 }}>
      {entries.map((e) => (
        <View key={e.id} style={{ borderBottomWidth: 1, borderColor: t.border, paddingBottom: 14 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 9, marginBottom: 7 }}>
            <Text style={{ fontSize: 12, color: t.sub }}>{formatDate(e.publishedAt)}</Text>
            {newIds.current?.has(e.id) ? <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: t.accent }} /> : null}
          </View>
          <Text style={{ fontSize: 15, fontWeight: "800", color: t.text, marginBottom: 8 }}>{e.title}</Text>
          <Text style={{ fontSize: 13.5, color: t.sub, lineHeight: 20 }}>{e.body}</Text>
        </View>
      ))}
    </ScrollView>
  );
}
